import { Injectable, inject, signal } from '@angular/core';
import { create } from '@bufbuild/protobuf';
import {
  LoginRequest,
  LoginResponse,
  VerifyTotpRequest,
  VerifyTotpResponse,
  LoginResponseSchema,
  VerifyTotpResponseSchema,
  LogoutResponse,
  TotpResponse,
  TotpResponseSchema,
  GetAuthenticatorsResponse,
  GetAuthenticatorsResponseSchema,
  DisableTotpRequest,
  GenerateTotpInfoResponse,
  ChangePasswordResponse,
  ChangePasswordResponseSchema,
  ChangePasswordRequest,
  EmailManagerResponse,
  EmailManagerResponseSchema,
  ValidateResponse,
  ValidateResponseSchema,
  ConfirmEmailRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponseSchema,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from '@gen/src/protos/auth/v1/auth_pb';
import {
  AddProfileInfoRequest,
  AddressRequest,
  AddressResponse,
  AddressResponseSchema,
  AddUserEducationRequest,
  AddUserEmploymentRequest,
  CreateUserRequest,
  GetAddressesResponse,
  GetAddressesResponseSchema,
  GetProfileInfoRequest,
  GetUserIdentifierRequest,
  GetUserIdentifierResponse,
  GetUserIdentifierResponseSchema,
  ProfileInfoResponse,
  ProfileInfoResponseSchema,
  UpdateProfileInfoRequest,
  UpdateUserEducationRequest,
  UpdateUserEmploymentRequest,
  UpdateUserIdentifierRequest,
  UpdateUserIdentifierResponse,
  UploadProfileImageRequestSchema,
  UserEducationResponse,
  UserEducationResponseSchema,
  UserEmploymentResponse,
  UserEmploymentResponseSchema,
  UserResponse,
} from '@gen/src/protos/users/users_pb';
import { AUTH_CLIENT, USER_CLIENT } from '@tokens/injection-tokens';

@Injectable({ providedIn: 'root' })
export class AuthLogicService {
  private authClient = inject(AUTH_CLIENT);
  private userClient = inject(USER_CLIENT);
  readonly currentUser = signal<string | null>(null);

  async login(request: LoginRequest) {
    try {
      const response: LoginResponse = await this.authClient.login(request as LoginRequest);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<LoginResponse>(LoginResponseSchema, 'No hay conexión');
    }
  }

  async verify2FA(request: VerifyTotpRequest) {
    try {
      const response: VerifyTotpResponse = await this.authClient.verifyTOTP(request);

      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<VerifyTotpResponse>(VerifyTotpResponseSchema, 'No hay conexión');
    }
  }

  async getProfileInfo(username: string | undefined = undefined) {
    try {
      const token = localStorage.getItem('auth_token');

      if (!token) return;

      const request: GetProfileInfoRequest = {
        $typeName: 'GetProfileInfoRequest',
        $unknown: [],
        username: username,
      };
      const response: ProfileInfoResponse = await this.userClient.getProfileInfo(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<ProfileInfoResponse>(
        ProfileInfoResponseSchema,
        'No hay conexión',
      );
    }
  }

  async getUserIdentifiers(username: string | undefined = undefined) {
    try {
      const request: GetUserIdentifierRequest = {
        $typeName: 'GetUserIdentifierRequest',
        $unknown: [],
        username: username,
      };
      const response: GetUserIdentifierResponse = await this.userClient.getUserIdentifiers(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<GetUserIdentifierResponse>(
        GetUserIdentifierResponseSchema,
        'No hay conexión',
      );
    }
  }

  async globalLogout() {
    try {
      const response: LogoutResponse = await this.authClient.globalLogout({});
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<GetUserIdentifierResponse>(
        GetUserIdentifierResponseSchema,
        'No hay conexión',
      );
    }
  }

  async getAddresses() {
    try {
      const response: GetAddressesResponse = await this.userClient.getAddresses({});
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<GetAddressesResponse>(
        GetAddressesResponseSchema,
        'No hay conexión',
      );
    }
  }

  async addAddress(request: AddressRequest) {
    try {
      const response: AddressResponse = await this.userClient.addAddress(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<AddressResponse>(AddressResponseSchema, 'No hay conexión');
    }
  }

  async updateAddress(request: AddressRequest) {
    try {
      const response: AddressResponse = await this.userClient.updateAddress(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<AddressResponse>(AddressResponseSchema, 'No hay conexión');
    }
  }

  async register(request: CreateUserRequest) {
    try {
      const response: UserResponse = await this.userClient.createUser(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<UserResponse>(GetAddressesResponseSchema, 'No hay conexión');
    }
  }

  async addProfileInfo(request: AddProfileInfoRequest) {
    try {
      const response: ProfileInfoResponse = await this.userClient.addProfileInfo(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<ProfileInfoResponse>(
        ProfileInfoResponseSchema,
        'No hay conexión',
      );
    }
  }

  async updateProfileInfo(request: UpdateProfileInfoRequest) {
    try {
      const response: ProfileInfoResponse = await this.userClient.updateProfileInfo(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<ProfileInfoResponse>(
        GetAddressesResponseSchema,
        'No hay conexión',
      );
    }
  }

  async onImageSaved(file: File) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const request = create(UploadProfileImageRequestSchema, {
      image: bytes,
    });
    const response = await this.userClient.uploadProfileImage(request);

    return response;
  }

  async updateUserIdentifier(request: UpdateUserIdentifierRequest) {
    try {
      const response: UpdateUserIdentifierResponse =
        await this.userClient.updateUserIdentifier(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<UpdateUserIdentifierResponse>(
        GetUserIdentifierResponseSchema,
        'No hay conexión',
      );
    }
  }

  async enableTOTP() {
    try {
      const response: TotpResponse = await this.authClient.enableTOTP({});
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<TotpResponse>(TotpResponseSchema, 'No hay conexión');
    }
  }

  async disableTOTP(request: DisableTotpRequest) {
    try {
      const response: TotpResponse = await this.authClient.disableTOTP(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<TotpResponse>(TotpResponseSchema, 'No hay conexión');
    }
  }

  async generateTOTPInfo() {
    try {
      const response: GenerateTotpInfoResponse = await this.authClient.generateTOTPInfo({});
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<GenerateTotpInfoResponse>(TotpResponseSchema, 'No hay conexión');
    }
  }

  async getAuthenticators() {
    try {
      const response: GetAuthenticatorsResponse = await this.authClient.getAuthenticators({});
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<GetAuthenticatorsResponse>(
        GetAuthenticatorsResponseSchema,
        'No hay conexión',
      );
    }
  }

  async changePassword(request: ChangePasswordRequest) {
    try {
      const response: ChangePasswordResponse = await this.authClient.changePassword(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<ChangePasswordResponse>(
        ChangePasswordResponseSchema,
        'No hay conexión',
      );
    }
  }

  async resetPassword(request: ResetPasswordRequest) {
    try {
      const response: ResetPasswordResponse = await this.authClient.resetPassword(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<ResetPasswordResponse>(
        ChangePasswordResponseSchema,
        'No hay conexión',
      );
    }
  }

  async forgotPassword(request: ForgotPasswordRequest) {
    try {
      const response: ForgotPasswordResponse = await this.authClient.forgotPassword(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<ForgotPasswordResponse>(
        ForgotPasswordResponseSchema,
        'No hay conexión',
      );
    }
  }

  async emailConfirmation(request: ConfirmEmailRequest) {
    try {
      const response = await this.authClient.confirmEmail(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<EmailManagerResponse>(
        EmailManagerResponseSchema,
        'No hay conexión',
      );
    }
  }

  async sendEmailVerification() {
    try {
      const response: EmailManagerResponse = await this.authClient.sendEmailVerification({});
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<EmailManagerResponse>(
        EmailManagerResponseSchema,
        'No hay conexión',
      );
    }
  }

  async validateSession() {
    try {
      const response: ValidateResponse = await this.authClient.validateSession({});
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<ValidateResponse>(ValidateResponseSchema, 'No hay conexión');
    }
  }

  async getUserEducations() {
    try {
      const response = await this.userClient.getUserEducations({});
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<UserEducationResponse>(
        UserEducationResponseSchema,
        'No hay conexión',
      );
    }
  }

  async addUserEducation(request: AddUserEducationRequest) {
    try {
      const response: UserEducationResponse = await this.userClient.addUserEducation(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<UserEducationResponse>(
        UserEducationResponseSchema,
        'No hay conexión',
      );
    }
  }

  async updateUserEducation(request: UpdateUserEducationRequest) {
    try {
      const response: UserEducationResponse = await this.userClient.updateUserEducation(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<UserEducationResponse>(
        UserEducationResponseSchema,
        'No hay conexión',
      );
    }
  }

  async getUserEmployments() {
    try {
      const response = this.userClient.getUserEmployments({});
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<UserEmploymentResponse>(
        UserEmploymentResponseSchema,
        'No hay conexión',
      );
    }
  }

  async addUserEmployment(request: AddUserEmploymentRequest) {
    try {
      const response: UserEmploymentResponse = await this.userClient.addUserEmployment(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<UserEmploymentResponse>(
        UserEmploymentResponseSchema,
        'No hay conexión',
      );
    }
  }

  async updateUserEmployment(request: UpdateUserEmploymentRequest) {
    try {
      const response: UserEmploymentResponse = await this.userClient.updateUserEmployment(request);
      return response;
    } catch (error: unknown) {
      console.error('gRPC transport error:', error);
      return generateErrorResponse<UserEmploymentResponse>(
        UserEmploymentResponseSchema,
        'No hay conexión',
      );
    }
  }
}

function generateErrorResponse<T extends { code?: string; message?: string }>(
  messageSchema: { typeName: string },
  msg = 'Error de conexión',
): T {
  const result = {
    $typeName: messageSchema.typeName,
    $unknown: [] as never[],
    code: 'AUTH-500',
    message: msg,
  };

  return result as unknown as T;
}
